import "@/App.css";
import { BrowserRouter, Routes, Route, Outlet } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { CartProvider } from "@/context/CartContext";
import { LangProvider, useLang } from "@/lib/i18n";
import { SmoothScroll } from "@/components/site/SmoothScroll";
import { Navbar } from "@/components/site/Navbar";
import { Footer } from "@/components/site/Footer";
import { CartDrawer } from "@/components/site/CartDrawer";
import Home from "@/pages/Home";
import Shop from "@/pages/Shop";
import ProductPage from "@/pages/ProductPage";
import Composer from "@/pages/Composer";
import { PaymentSuccess, PaymentCancel } from "@/pages/Payment";
import AdminLogin from "@/pages/AdminLogin";
import Checkout from "@/pages/Checkout";
import AdminDashboard from "@/pages/AdminDashboard";
import CollectionPage from "@/pages/CollectionPage";
import TopicPage from "@/pages/TopicPage";
import EnPage from "@/pages/EnPage";
import { EnBlogIndex, EnArticlePage } from "@/pages/EnBlog";
import { EN_PAGES } from "@/lib/enPages";
import { ConseilsIndex, ArticlePage } from "@/pages/Conseils";
import About from "@/pages/About";
import Presse from "@/pages/Presse";
import OfferPage from "@/pages/OfferPage";
import Ambassadors from "@/pages/Ambassadors";
import Resellers from "@/pages/Resellers";
import LocalResellers from "@/pages/LocalResellers";
import AvisClients from "@/pages/AvisClients";
import CataloguePro from "@/pages/CataloguePro";
import Diagnostic from "@/pages/Diagnostic";
import Referral from "@/pages/Referral";
import WhyOzentya from "@/pages/WhyOzentya";
import GuideRoutine from "@/pages/GuideRoutine";
import ShareBeforeAfter from "@/pages/ShareBeforeAfter";
import { WelcomePopup } from "@/components/site/WelcomePopup";
import Subscription from "@/pages/Subscription";
import SubSavings from "@/pages/SubSavings";
import ExtremeSubscription from "@/pages/ExtremeSubscription";
import MySubscription from "@/pages/MySubscription";
import AmbassadorSpace from "@/pages/AmbassadorSpace";
import AmbSetPassword from "@/components/ambassadors/AmbAuth";
import FaqPage from "@/pages/FaqPage";
import Glossary from "@/pages/Glossary";
import FormationPage from "@/pages/FormationPage";
import LegalPage from "@/pages/LegalPage";
import NotFound from "@/pages/NotFound";
import { LEGAL } from "@/lib/legal";
import { TOPICS } from "@/lib/topics";
import { CoachFloatingButton } from "@/components/site/Coach";
import { DuoFloatingPill } from "@/components/subscription/SubPromo";
import { CookieConsent } from "@/components/site/CookieConsent";
import { VisitTracker } from "@/components/site/VisitTracker";

function StoreLayout() {
  const { lang } = useLang();
  return (
    <>
      <VisitTracker />
      {lang === "en" ? null : <WelcomePopup />}
      <Navbar />
      <Outlet />
      <Footer />
      <CartDrawer />
      {lang === "en" ? null : <DuoFloatingPill />}
    </>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <LangProvider>
        <CartProvider>
          <SmoothScroll />
          <Routes>
            <Route element={<StoreLayout />}>
              <Route path="/" element={<Home />} />
              <Route path="/boutique" element={<Shop />} />
              <Route path="/en/shop" element={<Shop />} />
              <Route path="/produit/:slug" element={<ProductPage />} />
              <Route path="/en/product/:slug" element={<ProductPage />} />
              <Route path="/composer" element={<Composer />} />
              <Route path="/shampoings" element={<CollectionPage key="shampoings" id="shampoings" />} />
              <Route path="/cremes-coiffantes" element={<CollectionPage key="cremes" id="cremes-coiffantes" />} />
              <Route path="/masques" element={<CollectionPage key="masques" id="masques" />} />
              <Route path="/huiles-serums" element={<CollectionPage key="huiles" id="huiles-serums" />} />
              <Route path="/gels" element={<CollectionPage key="gels" id="gels" />} />
              {Object.entries(TOPICS).map(([id, t]) => <Route key={id} path={t.path} element={<TopicPage key={id} id={id} />} />)}
              {Object.entries(EN_PAGES).map(([id, t]) => <Route key={`en-${id}`} path={t.path} element={<EnPage key={id} id={id} />} />)}
              <Route path="/conseils" element={<ConseilsIndex />} />
              <Route path="/en/blog" element={<EnBlogIndex />} />
              <Route path="/en/blog/:slug" element={<EnArticlePage />} />
              <Route path="/a-propos" element={<About />} />
              <Route path="/presse" element={<Presse />} />
              <Route path="/offre" element={<OfferPage />} />
              <Route path="/ambassadeurs" element={<Ambassadors />} />
              <Route path="/revendeurs" element={<Resellers />} />
              <Route path="/avis-clients" element={<AvisClients />} />
              <Route path="/catalogue-pro" element={<CataloguePro />} />
              <Route path="/ou-acheter/:zone" element={<LocalResellers />} />
              <Route path="/diagnostic" element={<Diagnostic />} />
              <Route path="/parrainage" element={<Referral />} />
              <Route path="/pourquoi-ozentya" element={<WhyOzentya />} />
              <Route path="/guide-routine-anti-chute" element={<GuideRoutine />} />
              <Route path="/partager-mon-avant-apres" element={<ShareBeforeAfter />} />
              <Route path="/abonnement" element={<Subscription />} />
              <Route path="/abonnement-extreme-repouss" element={<ExtremeSubscription />} />
              <Route path="/abonnement-economies" element={<SubSavings />} />
              <Route path="/mon-abonnement" element={<MySubscription />} />
              <Route path="/espace-ambassadeur" element={<AmbassadorSpace />} />
              <Route path="/espace-ambassadeur/mot-de-passe" element={<AmbSetPassword />} />
              {Object.entries(LEGAL).map(([id, l]) => <Route key={id} path={l.path} element={<LegalPage key={id} id={id} />} />)}
              <Route path="*" element={<NotFound />} />
              <Route path="/faq-cheveux-afro" element={<FaqPage />} />
              <Route path="/glossaire-cheveux-afro" element={<Glossary />} />
              <Route path="/formation" element={<FormationPage />} />
              <Route path="/en/afro-hair-glossary" element={<Glossary key="en" lang="en" />} />
              <Route path="/conseils/:slug" element={<ArticlePage />} />
              <Route path="/commande" element={<Checkout />} />
              <Route path="/payment/success" element={<PaymentSuccess />} />
              <Route path="/payment/cancel" element={<PaymentCancel />} />
            </Route>
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<AdminDashboard />} />
          </Routes>
          <CoachFloatingButton />
          <CookieConsent />
          <Toaster position="top-center" theme="dark" />
        </CartProvider>
        </LangProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
